import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTvDetails, getSeasonDetails, getImageUrl, getBackdropUrl } from '../services/tmdb';
import { useStore } from '../store/useStore';
import { loadUserProfile, addFavoriteTvShow } from '../data/userPreferences';
import MediaRow from '../components/media/MediaRow';
import LoadingSkeleton from '../components/ui/LoadingSkeleton';
import ChimeraLens from '../components/chimera/ChimeraLens';
import WhereToWatch from '../components/media/WhereToWatch';
import CastCarousel from '../components/media/CastCarousel';
import { getChimeraScan, getEnhancedChimeraScan, type ChimeraScan } from '../services/chimeraLens';
import type { TvShow as TvShowType, Episode } from '../types';

export default function TvShow() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [show, setShow] = useState<TvShowType | null>(null);
  const [selectedSeason, setSelectedSeason] = useState<number>(1);
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingEpisodes, setLoadingEpisodes] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [chimeraScan, setChimeraScan] = useState<ChimeraScan | null>(null);

  const {
    addToWatchlist,
    removeFromWatchlist,
    isInWatchlist,
    setTrailerKey
  } = useStore();

  const inWatchlist = show ? isInWatchlist('tv', show.id) : false;

  // Check if show is in favorites
  useEffect(() => {
    if (show) {
      const profile = loadUserProfile();
      const found = profile.favorites.tvShows.some(s => s.tmdbId === show.id || s.title === show.name);
      setIsFavorite(found);
    }
  }, [show]);

  const handleFavorite = () => {
    if (!show) return;
    const year = new Date(show.first_air_date).getFullYear();
    addFavoriteTvShow({
      title: show.name,
      tmdbId: show.id,
      year,
      addedAt: Date.now(),
    });
    setIsFavorite(true);
  };

  useEffect(() => {
    const fetchShow = async () => {
      if (!id) return;
      try {
        setLoading(true);
        setImageLoaded(false);
        const data = await getTvDetails(parseInt(id));
        setShow(data);

        // Set initial season (first non-special season)
        const firstSeason = data.seasons?.find(s => s.season_number > 0)?.season_number || 1;
        setSelectedSeason(firstSeason);
      } catch (error) {
        console.error('Error fetching TV show:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchShow();
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    const fetchEpisodes = async () => {
      if (!id || !selectedSeason) return;
      try {
        setLoadingEpisodes(true);
        const seasonData = await getSeasonDetails(parseInt(id), selectedSeason);
        setEpisodes(seasonData.episodes || []);
      } catch (error) {
        console.error('Error fetching episodes:', error);
      } finally {
        setLoadingEpisodes(false);
      }
    };

    fetchEpisodes();
  }, [id, selectedSeason]);

  useEffect(() => {
    if (!show) return;
    const previous = document.title;
    document.title = `${show.name} — CineVault`;
    return () => {
      document.title = previous;
    };
  }, [show]);

  useEffect(() => {
    if (!show) return;

    const fallback = getChimeraScan(show, 'tv');
    setChimeraScan(fallback);

    let cancelled = false;
    getEnhancedChimeraScan(show, 'tv').then((scan) => {
      if (!cancelled) setChimeraScan(scan);
    });

    return () => {
      cancelled = true;
    };
  }, [show]);

  const handleWatchlist = () => {
    if (!show) return;
    if (inWatchlist) {
      removeFromWatchlist('tv', show.id);
    } else {
      addToWatchlist({
        id: show.id,
        mediaType: 'tv',
        title: show.name,
        posterPath: show.poster_path,
        backdropPath: show.backdrop_path,
        voteAverage: show.vote_average,
        releaseDate: show.first_air_date,
        addedAt: Date.now(),
      });
    }
  };

  const trailerKey = show?.videos?.results?.find(
    (v) => v.type === 'Trailer' && v.site === 'YouTube'
  )?.key;

  if (loading) {
    return <LoadingSkeleton type="detail" />;
  }

  if (!show) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">TV Show not found</h1>
          <button
            onClick={() => navigate('/')}
            className="btn-primary px-6 py-2 rounded-lg"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  const year = new Date(show.first_air_date).getFullYear();
  const endYear = show.status === 'Ended' && show.last_air_date
    ? new Date(show.last_air_date).getFullYear()
    : null;
  const yearRange = endYear && endYear !== year ? `${year} - ${endYear}` : year;

  const regularSeasons = show.seasons?.filter(s => s.season_number > 0) || [];

  return (
    <div className="min-h-screen">
      {/* Backdrop */}
      <div className="relative h-[60vh] sm:h-[70vh]">
        <div className="absolute inset-0">
          <img
            src={getBackdropUrl(show.backdrop_path, 'original')}
            alt={show.name}
            className={`w-full h-full object-cover transition-opacity duration-500 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={() => setImageLoaded(true)}
          />
          {!imageLoaded && <div className="absolute inset-0 shimmer" />}
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#141414] via-[#141414]/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#141414] via-transparent to-transparent" />
      </div>

      {/* Content */}
      <div className="relative -mt-48 z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Poster */}
          <div className="flex-shrink-0 w-48 sm:w-64 mx-auto md:mx-0">
            <img
              src={getImageUrl(show.poster_path, 'w500')}
              alt={show.name}
              className="w-full rounded-lg shadow-2xl"
            />
          </div>

          {/* Details */}
          <div className="flex-1 text-center md:text-left">
            <h1 className="font-display text-detail-title font-bold mb-2">
              {show.name}
            </h1>

            {show.tagline && (
              <p className="text-gray-400 italic text-detail-tagline mb-4">"{show.tagline}"</p>
            )}

            {/* Meta */}
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-detail-meta mb-6">
              <span className="flex items-center gap-1 text-yellow-400 font-semibold">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                {show.vote_average.toFixed(1)}
              </span>
              <span className="text-gray-300">{yearRange}</span>
              <span className="text-gray-300">{show.number_of_seasons} Season{show.number_of_seasons !== 1 ? 's' : ''}</span>
              <span className={`px-2 py-0.5 rounded text-xs uppercase ${
                show.status === 'Returning Series' ? 'bg-green-500/20 text-green-400' : 'bg-white/20'
              }`}>
                {show.status}
              </span>
            </div>

            {/* Genres */}
            {show.genres && show.genres.length > 0 && (
              <div className="flex flex-wrap gap-2 justify-center md:justify-start mb-6">
                {show.genres.map((genre) => (
                  <span
                    key={genre.id}
                    className="px-3 py-1 bg-white/10 rounded-full text-sm"
                  >
                    {genre.name}
                  </span>
                ))}
              </div>
            )}

            {/* Overview */}
            <p className="text-gray-300 text-detail-body leading-relaxed mb-8 max-w-3xl">
              {show.overview}
            </p>

            {chimeraScan && show && <ChimeraLens scan={chimeraScan} mediaType="tv" mediaId={show.id} />}

            <WhereToWatch mediaType="tv" mediaId={show.id} />

            {/* Actions */}
            <div className="mt-8 flex flex-wrap items-center justify-center md:justify-start gap-4">
              <button
                onClick={() => navigate(`/watch/tv/${show.id}?s=${selectedSeason}&e=1`)}
                className="btn-primary flex items-center gap-2 px-8 py-3 rounded-lg font-semibold text-lg"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                </svg>
                Watch Now
              </button>

              {trailerKey && (
                <button
                  onClick={() => setTrailerKey(trailerKey)}
                  className="flex items-center gap-2 px-6 py-3 rounded-lg font-semibold bg-white/20 hover:bg-white/30 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Trailer
                </button>
              )}

              {show.external_ids?.imdb_id && (
                <a
                  href={`https://www.imdb.com/title/${show.external_ids.imdb_id}/`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 rounded-lg border border-yellow-400/30 bg-[#f5c518] px-5 py-3 font-bold text-black transition hover:-translate-y-0.5 hover:bg-[#ffd84d]"
                  aria-label={`Open ${show.name} on IMDb`}
                >
                  <span className="rounded bg-black px-1.5 py-0.5 text-xs font-black text-[#f5c518]">IMDb</span>
                  Title page ↗
                </a>
              )}

              <button
                onClick={handleWatchlist}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-colors ${
                  inWatchlist
                    ? 'bg-white text-black hover:bg-gray-200'
                    : 'bg-white/20 hover:bg-white/30'
                }`}
              >
                {inWatchlist ? 'In My List ✓' : '+ Add to List'}
              </button>

              <button
                onClick={handleFavorite}
                disabled={isFavorite}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-colors ${
                  isFavorite
                    ? 'bg-red-500 text-white cursor-default'
                    : 'bg-white/20 hover:bg-red-500'
                }`}
                title={isFavorite ? 'Already in your favorites!' : 'Mark as watched & loved'}
              >
                <svg className="w-5 h-5" fill={isFavorite ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                {isFavorite ? 'Favorited' : 'Favorite'}
              </button>
            </div>
          </div>
        </div>

        {show.credits?.cast && <CastCarousel cast={show.credits.cast} />}

        {/* Episodes Section */}
        <div className="mt-12">
          <div className="flex items-center gap-4 mb-6">
            <h2 className="text-2xl font-semibold">Episodes</h2>
            <select
              value={selectedSeason}
              onChange={(e) => setSelectedSeason(parseInt(e.target.value))}
              className="bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
            >
              {regularSeasons.map((season) => (
                <option key={season.season_number} value={season.season_number} className="bg-[#141414]">
                  Season {season.season_number}
                </option>
              ))}
            </select>
          </div>

          {/* Episode List */}
          <div className="space-y-4">
            {loadingEpisodes ? (
              Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex gap-4 p-4 bg-white/5 rounded-lg animate-pulse">
                  <div className="w-40 h-24 bg-white/10 rounded" />
                  <div className="flex-1">
                    <div className="h-5 w-1/3 bg-white/10 rounded mb-2" />
                    <div className="h-4 w-full bg-white/10 rounded" />
                  </div>
                </div>
              ))
            ) : (
              episodes.map((episode) => (
                <div
                  key={episode.id}
                  onClick={() => navigate(`/watch/tv/${show.id}?s=${selectedSeason}&e=${episode.episode_number}`)}
                  className="flex flex-col sm:flex-row gap-4 p-4 bg-white/5 hover:bg-white/10 rounded-lg cursor-pointer transition-colors group"
                >
                  <div className="relative w-full sm:w-40 flex-shrink-0">
                    <img
                      src={getImageUrl(episode.still_path, 'w300')}
                      alt={episode.name}
                      className="w-full aspect-video object-cover rounded"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/placeholder-episode.svg';
                      }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-10 h-10 bg-white/90 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-black ml-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-semibold">
                        {episode.episode_number}. {episode.name}
                      </h3>
                      {episode.runtime && (
                        <span className="text-sm text-gray-400">{episode.runtime}m</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-400 line-clamp-2 mt-1">
                      {episode.overview || 'No description available.'}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recommendations */}
        {show.recommendations?.results && show.recommendations.results.length > 0 && (
          <div className="mt-12">
            <MediaRow
              title="More Like This"
              items={show.recommendations.results.slice(0, 20)}
              type="tv"

            />
          </div>
        )}
      </div>
    </div>
  );
}
